import React from "react";
import { motion } from "framer-motion";
import { ShieldCheck, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../i18n";
import { VALUES, CERTIFICATIONS, SERVICES, IMAGES } from "../data/content";
import { PageHero } from "../components/PageHero";
import { SectionHeading } from "../components/SectionHeading";
import { Reveal, stagger, item } from "../components/fx/Reveal";
import { Icon } from "../components/Icon";
import { Marquee } from "../components/fx/Marquee";

const CHAPTERS = [
  {
    ro: { t: "Infrastructura din spatele fiecărei clădiri", d: "Pentru noi, electricitatea nu înseamnă doar cabluri și tablouri electrice. Înseamnă infrastructura care pune în mișcare locuințe, afaceri și procese industriale." },
    en: { t: "The infrastructure behind every building", d: "For us, electricity is not just cables and switchboards. It is the infrastructure that powers homes, businesses and industrial processes." },
  },
  {
    ro: { t: "Engineering, nu improvizație", d: "Fiecare proiect pornește de la o analiză tehnică riguroasă și o documentație clară. Executăm conform normelor și livrăm sisteme pe care te poți baza ani la rând." },
    en: { t: "Engineering, not improvisation", d: "Every project starts with rigorous technical analysis and clear documentation. We build to code and deliver systems you can rely on for years." },
  },
  {
    ro: { t: "Tehnologie care lucrează pentru tine", d: "Integrăm automatizări, soluții smart și monitorizare, transformând instalațiile clasice în infrastructură inteligentă și eficientă energetic." },
    en: { t: "Technology that works for you", d: "We integrate automation, smart solutions and monitoring, turning classic installations into intelligent, energy-efficient infrastructure." },
  },
];

export default function About() {
  const { lang, L } = useLang();

  return (
    <>
      <PageHero
        label={lang === "ro" ? "Despre noi" : "About us"}
        title={lang === "ro" ? "Energie cu disciplină de engineering." : "Energy with engineering discipline."}
        image={IMAGES.aboutMood}
      />

      {/* Manifesto chapters */}
      <section className="py-24 md:py-32">
        <div className="container-pdr grid gap-16 lg:grid-cols-[1fr_0.9fr]">
          <div className="space-y-14">
            {CHAPTERS.map((c, i) => (
              <Reveal key={i} className="border-l border-white/10 pl-8">
                <span className="font-mono text-sm text-glow-2">0{i + 1} —</span>
                <h3 className="mt-3 font-display text-2xl font-bold text-white md:text-3xl">{L(c)?.t}</h3>
                <p className="mt-4 max-w-xl text-base leading-relaxed text-white/60">{L(c)?.d}</p>
              </Reveal>
            ))}
          </div>
          <Reveal variant="scale" className="relative">
            <div className="sticky top-28 overflow-hidden rounded-3xl border border-white/10">
              <img src={IMAGES.aboutTeam} alt="VOLTPDR team" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
              <div className="absolute bottom-0 p-8">
                <p className="font-display text-2xl font-bold text-gradient-glow">VOLTPDR</p>
                <p className="mt-1 text-sm text-white/60">Electric Technology · Engineering & Automation</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="border-y border-white/10 bg-surface1/40 py-24 md:py-32">
        <div className="container-pdr">
          <SectionHeading label={lang === "ro" ? "Valorile noastre" : "Our values"} title={lang === "ro" ? "Principii care ghidează fiecare proiect." : "Principles guiding every project."} />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="mt-14 grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-3">
            {VALUES.map((v, i) => (
              <motion.div key={i} variants={item} className="group flex items-center gap-5 bg-ink p-8 transition-colors duration-300 hover:bg-surface2" data-testid={`value-${i}`}>
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-glow-2/25 bg-glow-2/10 text-glow-2 transition-transform duration-300 group-hover:scale-110">
                  <Icon name={v.icon} className="h-6 w-6" />
                </div>
                <span className="font-display text-xl font-bold text-white">{v[lang]}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services */}
      <section className="border-b border-white/10 py-24 md:py-32">
        <div className="container-pdr">
          <SectionHeading
            label={lang === "ro" ? "Ce oferim" : "What we offer"}
            title={lang === "ro" ? "Servicii complete de instalații și automatizări." : "Complete installation and automation services."}
            desc={lang === "ro" ? "De la proiectare la mentenanță, acoperim întregul ciclu de viață al infrastructurii electrice." : "From design to maintenance, we cover the full lifecycle of electrical infrastructure."}
          />
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <motion.div key={i} variants={item} className="group h-full rounded-2xl border border-white/10 bg-surface2/50 p-6 transition-colors duration-300 hover:border-glow-2/40" data-testid={`about-service-${i}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-glow-2/25 bg-glow-2/10 text-glow-2 transition-transform duration-300 group-hover:scale-110">
                  <Icon name={s.icon} className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-base font-bold leading-snug text-white">{L(s)?.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-white/55">{L(s)?.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <div className="py-12">
        <Marquee speed={26}>
          <span className="mx-8 font-display text-5xl font-extrabold text-white/[0.06] md:text-7xl">
            {lang === "ro" ? "PUTERE · PRECIZIE · SIGURANȚĂ · TEHNOLOGIE ·" : "POWER · PRECISION · SAFETY · TECHNOLOGY ·"}
          </span>
        </Marquee>
      </div>

      {/* Certifications */}
      <section className="pb-28">
        <div className="container-pdr">
          <SectionHeading label={lang === "ro" ? "Certificări" : "Certifications"} title={lang === "ro" ? "Autorizați, atestați, de încredere." : "Authorized, attested, trusted."} />
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {CERTIFICATIONS.map((c, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="group h-full rounded-2xl border border-white/10 bg-surface2/60 p-7 transition-colors duration-300 hover:border-glow-2/40" data-testid={`cert-${i}`}>
                  <ShieldCheck className="h-7 w-7 text-glow-2" />
                  <p className="mt-5 font-display text-2xl font-extrabold text-white">{c.code}</p>
                  <p className="mt-2 text-sm text-white/55">{L(c)}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-16">
            <div className="flex flex-col items-start justify-between gap-6 rounded-3xl border border-white/10 bg-gradient-to-r from-surface2 to-ink p-10 md:flex-row md:items-center">
              <div>
                <h3 className="font-display text-2xl font-bold text-white md:text-3xl">{lang === "ro" ? "Ai un proiect în minte?" : "Have a project in mind?"}</h3>
                <p className="mt-2 text-white/60">{lang === "ro" ? "Hai să discutăm despre soluția potrivită." : "Let's talk about the right solution."}</p>
              </div>
              <Link to="/contact" className="btn-glow inline-flex items-center gap-2 rounded-full bg-brand px-7 py-4 font-semibold text-ink transition-transform hover:scale-[1.03] glow-ring">
                {lang === "ro" ? "Contactează-ne" : "Contact us"} <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
